import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useTracking } from '../../hooks/useTracking';

const AboutPage = () => {
  useTracking();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // 設置頁面標題和 meta 標籤
    document.title = '關於建鈞精緻搬家｜10年專業搬家經驗｜新北台北桃園新竹搬家公司｜廢棄物處理專家';
    
    // 更新 meta 標籤
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateMetaTag('description', '建鈞精緻搬家成立於2014年，擁有10年專業搬家經驗，是新北市、台北市、桃園市、新竹市值得信賴的搬家公司。專業團隊提供家庭搬家、辦公室搬遷、廢棄物處理、垃圾清運、大型家具回收等服務。我們堅持以客為尊，提供安全、快速、貼心的搬家服務。24小時服務專線：0978-900-081，LINE：@285yavdh。');
    updateMetaTag('keywords', '建鈞精緻搬家,關於建鈞搬家,新北搬家公司,台北搬家公司,專業搬家團隊,搬家公司推薦,10年搬家經驗,廢棄物處理專家,垃圾清運公司,家具回收服務,搬家公司介紹,搬家團隊,專業搬家服務,環保清運,大型廢棄物處理');
    
    // 更新 canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://sadiest018moving.com/about';

    // 更新 Open Graph 標籤
    updatePropertyTag('og:title', '關於建鈞精緻搬家｜10年專業搬家經驗｜新北台北桃園新竹搬家公司｜廢棄物處理專家');
    updatePropertyTag('og:description', '建鈞精緻搬家成立於2014年，擁有10年專業搬家經驗，是新北市、台北市、桃園市、新竹市值得信賴的搬家公司。專業團隊提供家庭搬家、辦公室搬遷、廢棄物處理、垃圾清運等服務。');
    updatePropertyTag('og:url', 'https://sadiest018moving.com/about');
    updatePropertyTag('og:type', 'website');

    // 監聽滾動
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
      
      // 檢測當前區塊
      const sections = ['intro', 'values', 'features', 'areas'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 返回頂部
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section - 優化配色和動畫 */}
        <section className="relative bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 text-white py-20 overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                <i className="ri-building-line text-xl"></i>
                <span className="font-medium">關於我們</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                關於建鈞精緻搬家
              </h1>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8">
                10年專業搬家經驗，新北台北桃園新竹值得信賴的搬家公司
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full hover:bg-white/30 transition-all transform hover:scale-105">
                  <span className="font-semibold">成立於 2014 年</span>
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full hover:bg-white/30 transition-all transform hover:scale-105">
                  <span className="font-semibold">服務超過 5000+ 客戶</span>
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full hover:bg-white/30 transition-all transform hover:scale-105">
                  <span className="font-semibold">客戶滿意度 98%</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 公司簡介 */}
        <section id="intro" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  專業搬家與廢棄物處理服務
                </h2>
                <div className="w-20 h-1 bg-teal-500 mx-auto"></div>
              </div>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  <strong className="text-teal-600">建鈞精緻搬家</strong>成立於2014年，是新北市、台北市、桃園市、新竹市專業的搬家公司與廢棄物處理專家。我們擁有10年豐富的搬家經驗，累積服務超過5000位客戶，客戶滿意度高達98%。
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  我們的專業團隊不僅提供<strong>家庭搬家</strong>、<strong>辦公室搬遷</strong>、<strong>套房搬家</strong>等傳統搬家服務，更專精於<strong>廢棄物處理</strong>、<strong>垃圾清運</strong>、<strong>大型家具回收</strong>、<strong>裝潢廢棄物清理</strong>等環保清運服務。我們深知搬家過程中常伴隨大量廢棄物需要處理，因此提供一站式的搬家與清運解決方案。
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  服務範圍涵蓋<strong>新北市全區</strong>（板橋、新莊、中和、永和、土城、三重、蘆洲、泰山、五股等）、<strong>台北市</strong>、<strong>桃園市</strong>、<strong>新竹市</strong>。我們提供24小時服務，無論是緊急搬家需求或是大型廢棄物清運，都能快速為您服務。
                </p>

                <div className="bg-teal-50 border-l-4 border-teal-500 p-6 my-8">
                  <h3 className="text-xl font-bold text-teal-900 mb-3">
                    <i className="ri-recycle-line mr-2"></i>
                    廢棄物處理專業服務
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    我們擁有專業的廢棄物處理團隊和合法的清運資格，能夠處理各類廢棄物，包括大型家具、家電、裝潢廢料、辦公設備等。我們遵循環保法規，確保廢棄物得到妥善處理和回收，為環境保護盡一份心力。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 核心價值 */}
        <section id="values" className="py-16 bg-gradient-to-br from-gray-50 to-teal-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  我們的核心價值
                </h2>
                <p className="text-gray-600 text-lg">
                  以專業、誠信、用心為服務宗旨
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center mb-6 mx-auto">
                    <i className="ri-shield-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                    專業可靠
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-center">
                    10年搬家經驗，專業團隊訓練有素，搬運技術純熟，確保您的物品安全無虞。廢棄物處理符合環保法規，合法清運有保障。
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-500 rounded-full flex items-center justify-center mb-6 mx-auto">
                    <i className="ri-heart-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                    用心服務
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-center">
                    以客為尊，細心呵護每一件物品，提供客製化搬家方案。從搬家到廢棄物清運，一站式服務讓您省時省力。
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center mb-6 mx-auto">
                    <i className="ri-price-tag-3-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                    價格透明
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-center">
                    報價清楚透明，絕無隱藏費用。搬家費用、廢棄物清運費用事前詳細說明，讓您安心選擇我們的服務。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 服務特色 */}
        <section id="features" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  專業搬家與廢棄物處理特色
                </h2>
                <p className="text-gray-600 text-lg">
                  一站式搬家與清運解決方案
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-truck-line text-2xl text-teal-600"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      專業搬家服務
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      提供家庭搬家、套房搬家、辦公室搬遷、精緻搬家等服務。專業搬運團隊，確保物品安全搬運，搬家過程快速有效率。
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-delete-bin-line text-2xl text-teal-600"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      廢棄物處理專家
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      專業處理各類廢棄物，包括大型家具、家電、裝潢廢料、辦公設備等。合法清運，符合環保法規，讓您安心無憂。
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-time-line text-2xl text-teal-600"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      24小時服務
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      提供24小時搬家與廢棄物清運服務，無論是緊急搬家需求或是假日清運，隨時為您服務，彈性配合您的時間。
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      保險保障
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      提供搬家保險，萬一發生意外損壞，由保險公司理賠，讓您的貴重物品多一層保障，搬家更安心。
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-recycle-line text-2xl text-teal-600"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      環保回收
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      重視環保，可回收物品進行分類回收，減少環境負擔。廢棄物處理符合政府規範，為地球環保盡一份心力。
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-customer-service-line text-2xl text-teal-600"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      客製化方案
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      根據客戶需求提供客製化搬家與清運方案，從小型套房到大型辦公室，從單件家具到整屋廢棄物，都能為您量身規劃。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 服務區域 */}
        <section id="areas" className="py-16 bg-gradient-to-br from-gray-50 to-teal-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  服務區域
                </h2>
                <p className="text-gray-600 text-lg">
                  涵蓋新北、台北、桃園、新竹等地區
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-bold text-teal-600 mb-4">
                    <i className="ri-map-pin-line mr-2"></i>
                    新北市
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 板橋區搬家</li>
                    <li>• 新莊區搬家</li>
                    <li>• 中和區搬家</li>
                    <li>• 永和區搬家</li>
                    <li>• 土城區搬家</li>
                    <li>• 三重區搬家</li>
                    <li>• 蘆洲區搬家</li>
                    <li>• 泰山區搬家</li>
                    <li>• 五股區搬家</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-bold text-teal-600 mb-4">
                    <i className="ri-map-pin-line mr-2"></i>
                    台北市
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 大安區搬家</li>
                    <li>• 信義區搬家</li>
                    <li>• 中山區搬家</li>
                    <li>• 松山區搬家</li>
                    <li>• 內湖區搬家</li>
                    <li>• 南港區搬家</li>
                    <li>• 文山區搬家</li>
                    <li>• 士林區搬家</li>
                    <li>• 北投區搬家</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-bold text-teal-600 mb-4">
                    <i className="ri-map-pin-line mr-2"></i>
                    桃園市
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 桃園區搬家</li>
                    <li>• 中壢區搬家</li>
                    <li>• 平鎮區搬家</li>
                    <li>• 八德區搬家</li>
                    <li>• 蘆竹區搬家</li>
                    <li>• 龜山區搬家</li>
                    <li>• 大園區搬家</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-bold text-teal-600 mb-4">
                    <i className="ri-map-pin-line mr-2"></i>
                    新竹市
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 東區搬家</li>
                    <li>• 北區搬家</li>
                    <li>• 香山區搬家</li>
                    <li>• 竹北市搬家</li>
                    <li>• 竹東鎮搬家</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - 優化配色 */}
        <section className="py-16 bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                選擇建鈞精緻搬家，讓搬家與清運更輕鬆
              </h2>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                10年專業經驗，5000+客戶信賴，提供搬家與廢棄物處理一站式服務
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:0978900081"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-teal-600 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  立即來電：0978-900-081
                </a>
                <a
                  href="https://line.me/ti/p/@285yavdh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-green-500 text-white rounded-full font-semibold text-lg hover:bg-green-600 transition-all shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer border-2 border-white"
                >
                  <i className="ri-line-fill mr-2"></i>
                  LINE 免費諮詢
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 返回頂部按鈕 */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-24 right-8 bg-gradient-to-r from-teal-600 to-emerald-600 text-white w-12 h-12 rounded-full shadow-2xl hover:shadow-3xl transition-all hover:scale-110 z-50 flex items-center justify-center cursor-pointer"
            aria-label="返回頂部"
          >
            <i className="ri-arrow-up-line text-2xl"></i>
          </button>
        )}

        {/* 浮動 LINE 按鈕 */}
        <a
          href="https://line.me/ti/p/@285yavdh"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-8 right-8 bg-green-500 text-white w-14 h-14 rounded-full shadow-2xl hover:shadow-3xl transition-all hover:scale-110 z-50 flex items-center justify-center cursor-pointer group"
          aria-label="LINE 諮詢"
        >
          <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75"></div>
          <i className="ri-line-fill text-3xl relative z-10"></i>
          <span className="absolute right-16 bg-gray-900 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            LINE 免費諮詢
          </span>
        </a>

        <Footer />
      </div>
    </>
  );
};

export default AboutPage;
